import { parseFilters } from "@/lib/filters";

import type { ApiParams } from "../api";
import type { PageFilterName } from "../filters";

/**
 * Which page filters each tab sends to C1, by the C1 parameter name (the URL
 * key is the same name with the `f_` prefix). Origin is absent on purpose: the
 * two landed-cost pages filter it in the browser so the origin list stays whole.
 */
export const PARAMS_BY_TAB: Record<string, readonly PageFilterName[]> = {
  ranking_table: ["criteria_phase"],
  stalled: ["stall"],
  feature_ranking: ["feature"],
  feature_performance_average: ["feature"],
  feature_performance_minimum: ["feature"],
  criteria_ranking: ["criterion", "criteria_phase", "sample", "evaluator"],
  criteria_catalog: ["feature", "criteria_phase"],
  sourcing_funnel: ["cost_type", "incoterm"],
  ingredients_funnel: ["variant", "incoterm"],
  monthly_offers_purchases: ["variant", "incoterm", "year"],
  landed_cost_by_offer: ["variant", "destination", "incoterm"],
  landed_cost_vs_purchase: ["variant", "destination", "incoterm"],
};

/** The page-filter part of a tab's C1 request, read from the URL. */
export function pageExtra(tabKey: string, search: Record<string, unknown>): ApiParams {
  const values = parseFilters(search);
  const out: ApiParams = {};
  for (const name of PARAMS_BY_TAB[tabKey] ?? []) {
    const value = values[name];
    if (name === "sample") {
      // v1.7: the Sample pill picks option + sample keys (sourcing-funnel API v1.6 `sample_keys`).
      const keys = pageFilterList(search, "sample");
      if (keys.length > 0) out.sample_keys = keys.join(",");
      continue;
    }
    if (name === "stall" && value !== "30" && value !== "60") continue;
    if (typeof value === "string" && value !== "") out[name] = value;
  }
  return out;
}

/** The most rows C1 returns in one call. Pages that draw every row of a small set ask for this. */
export const ALL_ROWS = 200;

/** The one request both landed-cost pages and their filter row share (same query key, one fetch). */
export function landedExtra(tabKey: string, search: Record<string, unknown>): ApiParams {
  return { ...pageExtra(tabKey, search), limit: ALL_ROWS };
}

/** A multi-select page filter's picks (v1.7: the Sample pill), from a URL list or a comma string. */
export function pageFilterList(search: Record<string, unknown>, name: PageFilterName): string[] {
  const value = parseFilters(search)[name];
  const list = Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : [];
  return list.map((entry) => entry.trim()).filter((entry) => entry !== "");
}

/** One page filter's current value, or null. */
export function pageFilter(search: Record<string, unknown>, name: PageFilterName): string | null {
  const value = parseFilters(search)[name];
  return typeof value === "string" && value !== "" ? value : null;
}
